import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AppShell from '@/components/AppShell';

const IMAGES = {
  profile: '/about/profile.jpg',
  couple: '/about/couple.jpg',
  coaching: '/about/coaching.jpg',
  team: '/about/team.jpg',
  family1: '/about/family1.jpg',
  heroBanner: 'https://customer-assets.emergentagent.com/job_e079eb76-7fdc-4c18-ab8a-6dab3e979ce5/artifacts/nxbal9sr_kansas-photography.jpg',
};

const credentials = [
  'B.S. in History, Minor in Political Science — Wichita State University',
  'M.S.E. in Education — Fort Hays State University',
  'USD 492: Flinthills High School',
  'History & Government Teacher',
  'Technology Director & Website Coordinator',
  'USD 492 KS Teacher of the Year Nominee 2020',
  'Head HS Football Coach',
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <AppShell>
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="ml-4 mt-4 sm:ml-8 sm:mt-6 text-[#5A6577] hover:text-[#14213D]"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>

      {/* Hero banner — Flint Hills sunset */}
      <div className="relative h-56 sm:h-80 overflow-hidden mt-4">
        <img
          src={IMAGES.heroBanner}
          alt="Kansas Flint Hills at sunset"
          className="w-full h-full object-cover object-[center_60%]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14213D]/40 via-[#14213D]/50 to-[#14213D]/70" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <BookOpen className="h-8 w-8 text-[#D4AF37] mx-auto mb-3 drop-shadow-lg" strokeWidth={1.5} />
            <h2 className="font-serif-display text-2xl sm:text-4xl text-[#FDFBF7] drop-shadow-lg">About the Developer</h2>
            <p className="text-[#D4AF37] text-sm sm:text-base mt-2 drop-shadow-md">The educator behind Atlas History</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Bio */}
            <div>
              <h3 className="text-2xl sm:text-3xl font-serif text-[#14213D] mb-6 font-light">Chase A. Casteel</h3>
              <div className="space-y-3 mb-8">
                {credentials.map((c) => (
                  <div key={c} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#14213D] mt-2 flex-shrink-0" />
                    <p className="text-sm text-[#2D3748] leading-relaxed">{c}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-[#5A6577] leading-relaxed italic mb-8">
                Dedicated to bringing the past to the present for students all across Kansas.
              </p>

              <div className="space-y-4 mb-8">
                <p className="text-sm text-[#2D3748] leading-relaxed">
                  I&rsquo;m a high school history teacher, husband, father, and coach. My wife,
                  Jenna, is also an educator and teaches 3rd grade at Flinthills. Together, we are
                  proud parents to our son Conner, our daughter Jaycee, and our son Colin.
                </p>
                <p className="text-sm text-[#2D3748] leading-relaxed">
                  Family is a major part of who I am and why I teach the way I do. I believe
                  education is about more than dates, events, and assignments. It is about helping
                  students understand people, choices, consequences, and the world they are growing
                  up in. My goal as a teacher is to build relationships, challenge students to
                  think critically, and help them see that history still matters in their lives
                  today.
                </p>
              </div>

              <div className="bg-[#F5F0E8] rounded-xl p-6">
                <h4 className="font-serif text-[#14213D] text-sm mb-3">About This Curriculum</h4>
                <p className="text-xs text-[#2D3748] leading-relaxed mb-3">
                  This digital curriculum guide covers Kansas History, Government, and Social Studies
                  standards for grades 7 through 12. Every unit is aligned to the Kansas HGSS framework
                  and includes vocabulary, identifications, essay prompts, lesson plans, primary sources,
                  and connections to the present.                </p>
                <p className="text-xs text-[#2D3748] leading-relaxed">
                  Built to support inquiry-based instruction, the platform features interactive tools like
                  skeleton notes, lecture presentations, AI-powered essay grading, historical timelines,
                  map explorers, and state assessment projects aligned to the CBA model.
                </p>
              </div>
            </div>

            {/* Photo Gallery */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-3">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src={IMAGES.profile} alt="Chase A. Casteel" className="w-full h-64 object-cover object-[center_20%]" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-md">
                  <img src={IMAGES.couple} alt="" className="w-full h-52 object-cover object-top" />
                </div>
              </div>
              <div className="space-y-3 pt-6">
                <div className="rounded-xl overflow-hidden shadow-md">
                  <img src={IMAGES.coaching} alt="Coaching" className="w-full h-44 object-cover object-[center_25%]" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src={IMAGES.team} alt="Flinthills Football" className="w-full h-52 object-cover object-[center_30%]" />
                </div>
              </div>
              <div className="col-span-2 rounded-xl overflow-hidden shadow-lg">
                <img src={IMAGES.family1} alt="Chase, Jenna, Conner, Jaycee, and Colin Casteel" className="w-full h-72 sm:h-96 object-contain" />
              </div>
              <div className="col-span-2 -mt-1 flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[#5A6577]">
                <span className="h-px w-8 bg-[#D4AF37]/50" />
                <span>Jenna, Conner, Jaycee, and Colin</span>
                <span className="h-px w-8 bg-[#D4AF37]/50" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0F1A2E] py-8">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-[#D4AF37]/40 text-xs">Atlas History &middot; Modern Editorial Archive</p>
        </div>
      </footer>
      </div>
    </AppShell>
  );
}
